// ─── Multi-Selection Bar (Phase 1.9) ──────────────────────────
// Replaces the single-element AlignmentToolbar when 2+ elements are
// selected via Shift+click. Lightweight — no persistent group is created.
//
// Actions:
//   - "N selected" summary
//   - Clear selection
//   - "Convert to photo row" (only when 2-3 selected AND all are image_card)
//
// Convert to photo row centers a 76% wide row at the average Y of the
// selected photos, applies role=photo + cover + 1:1, and keeps each photo
// individually editable afterwards (no parentId/groupId).

import React from 'react';
import { Loader2, Rows3, X } from 'lucide-react';
import {
  PHOTO_ROW_MIN,
  PHOTO_ROW_MAX,
  computePhotoRowOverrides,
} from '../../lib/utils/photoRowLayout';

export default function MultiSelectionBar({
  selectionSet,            // [{sectionId, elementId}]
  elementsBySectionId,     // { [sectionId]: mergedElements[] }
  onClear,
  onConvertToPhotoRow,
  busy,
}) {
  const count = selectionSet.length;
  if (count < 2) return null;

  // Resolve elements (preserving selection order).
  const resolved = selectionSet
    .map(({ sectionId, elementId }) => {
      const list = elementsBySectionId?.[sectionId] || [];
      const el = list.find((e) => e.id === elementId);
      return el ? { sectionId, element: el } : null;
    })
    .filter(Boolean);

  // Photo row eligibility: 2-3 selected, all image_card, all share a section.
  const sameSection =
    resolved.length > 0 &&
    resolved.every((r) => r.sectionId === resolved[0].sectionId);
  const allImages =
    resolved.length > 0 && resolved.every((r) => r.element.type === 'image_card');
  const canPhotoRow =
    sameSection &&
    allImages &&
    count >= PHOTO_ROW_MIN &&
    count <= PHOTO_ROW_MAX;

  const handlePhotoRow = () => {
    if (!canPhotoRow || busy) return;
    const sectionId = resolved[0].sectionId;
    const elements = resolved.map((r) => r.element);
    const overrides = computePhotoRowOverrides(elements);
    onConvertToPhotoRow?.(sectionId, overrides);
  };

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2.5 space-y-2"
      data-testid="multi-selection-bar"
    >
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium text-gray-700 dark:text-gray-300"
          data-testid="multi-selection-count"
        >
          {count} selected
        </span>
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          title="Clear selection"
          data-testid="multi-selection-clear"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      </div>

      <button
        onClick={handlePhotoRow}
        disabled={!canPhotoRow || busy}
        title={
          canPhotoRow
            ? 'Arrange the selected photos as a centered row'
            : `Select 2-3 photos in the same section to enable`
        }
        className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
          canPhotoRow && !busy
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
        }`}
        data-testid="convert-to-photo-row-btn"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Rows3 className="w-3.5 h-3.5" />
        )}
        Convert to photo row
      </button>

      <p
        className="text-[10px] text-gray-400 leading-relaxed"
        data-testid="multi-selection-hint"
      >
        Arrow keys move all selected unlocked elements. Hold Shift+arrow for larger steps.
      </p>
    </div>
  );
}
